/**
 * Trade Analyser — shared primitives (Trade Analyser split, Job 1)
 *
 * TradeAnalyser.jsx used to be one 1132-line file covering both "Analyse
 * Your Trade" and "Trade Radar" behind an internal tab switch. Splitting it
 * into a landing page (TradeAnalyser.jsx) plus pages/trade/AnalyseTrade.jsx
 * and pages/trade/TradeRadar.jsx means both children need the same labels,
 * verdict colours, and per-sport stat-row helpers — this file is the one
 * place those live now, so the two pages can't quietly drift apart.
 */

import { useState, useEffect } from 'react'
import { V2, V2Card } from '../JaiV2'
import { JAI_FONTS } from '../JaiBrand'
import { JAIMascot } from '../JAIMascot'

// ── Responsive helper — the "2 columns ≥900px, stacked below" rule shows up
// in both the builder grid and the give/get columns. ──────────────────────

export function useMinWidth(px) {
  const [wide, setWide] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= px : true))
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= px)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [px])
  return wide
}

// ── Give/get labels — one constant so casing can't drift between pages ────

export const GIVE_LABEL = 'You give'
export const GET_LABEL = 'You get'

// ── Verdict → colour/label, the one place this mapping exists ─────────────

export const VERDICT_META = {
  PROPOSE:      { color: V2.success, label: 'Propose Trade',        mascot: 'happy' },
  ALTER:        { color: V2.warning, label: 'Alter Trade',          mascot: 'idle' },
  DONT_PROPOSE: { color: V2.error,   label: "Don't Propose Trade",  mascot: 'idle' },
}

// ── Per-player stat helpers — real field names, verified live against
// nbaStatsService.js's enrichRosterWithRecentStats and the NFL fields
// already exercised in Stage 2's curl pass (passYards/rushYards/recYards/
// receptions/gamesPlayed). NFL has no last-7/last-30 window on the current
// API-Sports plan — season only, never fabricated. ────────────────────────

export const NBA_STAT_COLUMNS = [
  { key: 'pts', label: 'PTS' }, { key: 'reb', label: 'REB' }, { key: 'ast', label: 'AST' },
  { key: 'threepm', label: '3PM' }, { key: 'fgpct', label: 'FG%' },
]

export function nbaStatRows(p) {
  const rows = []
  if (p.avgPoints != null) {
    rows.push({ label: 'Season', values: { pts: p.avgPoints, reb: p.avgRebounds, ast: p.avgAssists, threepm: p.fg3m, fgpct: p.fgPct != null ? p.fgPct * 100 : null } })
  }
  if (p.last30) {
    rows.push({ label: 'Last 30', values: { pts: p.last30.pts, reb: p.last30.reb, ast: p.last30.ast, threepm: p.last30.fg3m, fgpct: p.last30.fga ? (p.last30.fgm / p.last30.fga) * 100 : null } })
  }
  if (p.last7) {
    rows.push({ label: 'Last 7', values: { pts: p.last7.pts, reb: p.last7.reb, ast: p.last7.ast, threepm: p.last7.fg3m, fgpct: p.last7.fga ? (p.last7.fgm / p.last7.fga) * 100 : null } })
  }
  return rows
}

export const NFL_STAT_COLUMNS_BY_POS = {
  QB: [{ key: 'passYards', label: 'PASS YDS' }, { key: 'passTD', label: 'PASS TD' }, { key: 'interceptions', label: 'INT' }, { key: 'rushYards', label: 'RUSH YDS' }, { key: 'rushTD', label: 'RUSH TD' }],
  RB: [{ key: 'rushYards', label: 'RUSH YDS' }, { key: 'rushTD', label: 'RUSH TD' }, { key: 'receptions', label: 'REC' }, { key: 'recYards', label: 'REC YDS' }, { key: 'recTD', label: 'REC TD' }],
  WR: [{ key: 'receptions', label: 'REC' }, { key: 'recYards', label: 'REC YDS' }, { key: 'recTD', label: 'REC TD' }, { key: 'rushYards', label: 'RUSH YDS' }, { key: 'rushTD', label: 'RUSH TD' }],
  TE: [{ key: 'receptions', label: 'REC' }, { key: 'recYards', label: 'REC YDS' }, { key: 'recTD', label: 'REC TD' }],
}

export function nflStatColumns(p) {
  return NFL_STAT_COLUMNS_BY_POS[String(p.position || '').toUpperCase()] || []
}

export function nflStatRows(p, columns) {
  if (!columns.length || p.gamesPlayed == null) return []
  const values = {}
  columns.forEach(c => { values[c.key] = p[c.key] })
  return [{ label: 'Season', values }]
}

/** "Season avg 24.1 · Last 30 22.8 · Last 7 26.4" — the sport's primary stat, full words, never L7/L30. Null when nothing's available (omit the strip, don't dash it). */
export function headlineStat(p, sport) {
  if (sport === 'nfl') {
    // These ARE season totals (no per-game log available — see Hard Rule 3),
    // so "Season" is accurate here. Left as-is; only the NBA branch below was
    // mislabelling a per-game average as a season figure.
    const pos = String(p.position || '').toUpperCase()
    if (pos === 'QB' && p.passYards != null) return `Season ${p.passYards} Pass Yds`
    if (pos === 'RB' && p.rushYards != null) return `Season ${p.rushYards} Rush Yds`
    if ((pos === 'WR' || pos === 'TE') && p.recYards != null) return `Season ${p.recYards} Rec Yds`
    return null
  }
  if (p.avgPoints == null) return null
  // "Season avg" — avgPoints is a PER-GAME average, not a total; a bare
  // "Season 24.1" reads like a season sum next to the NFL branch's genuine
  // season totals above.
  const parts = [`Season avg ${p.avgPoints.toFixed(1)}`]
  if (p.last30?.pts != null) parts.push(`Last 30 ${p.last30.pts.toFixed(1)}`)
  if (p.last7?.pts != null) parts.push(`Last 7 ${p.last7.pts.toFixed(1)}`)
  return parts.join(' · ')
}

// Mirrors JaiRichText's own <ul>/<li> dot-bullet convention (see that file)
// for the pros/cons arrays, which arrive as plain string arrays now — no
// markdown to parse, so no need to route them through JaiRichText itself.
export function BulletList({ items, color = V2.navy, dotColor }) {
  if (!items?.length) return <p style={{ fontSize: 12.5, color: V2.faint, margin: 0 }}>Nothing to flag.</p>
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {items.map((item, i) => (
        <li key={i} style={{ position: 'relative', paddingLeft: 14, marginBottom: 7, fontSize: 12.5, color, lineHeight: 1.5 }}>
          <span style={{ position: 'absolute', left: 0, top: '0.55em', width: 4, height: 4, borderRadius: '50%', background: dotColor || color }} />
          {item}
        </li>
      ))}
    </ul>
  )
}

// ── Draft-pending gate — shared render for both children, driven by
// hooks/useDraftGate.js ────────────────────────────────────────────────

export function DraftPendingState() {
  return (
    <V2Card pad="32px 24px" style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
        <JAIMascot size={48} state="idle" animate={false} />
      </div>
      <h3 style={{ fontSize: 15, fontWeight: 800, color: V2.navy, letterSpacing: '0.04em', textTransform: 'uppercase', margin: '0 0 8px' }}>Available after your draft</h3>
      <p style={{ fontSize: 13, color: V2.mute, maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>
        Trades need real rosters to compare — yours and the other managers' in your league.
        Once your draft is complete, JAI will read every roster and you can build and grade real trades here.
      </p>
    </V2Card>
  )
}

export function ImpactRow({ label, deltaPercent, basis }) {
  // Deliberately NOT pctTone: this is a signed delta (can be negative, can
  // exceed 100), not an absolute 0-100 metric — pctTone's fixed 80/50/49
  // bands would misread a small positive delta as "red". Sign-based
  // green/red/neutral is the correct read here.
  const color = deltaPercent === 0 ? V2.mute : deltaPercent > 0 ? V2.success : V2.error
  return (
    <div title={basis} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: `1px solid ${V2.hairSoft}` }}>
      <span style={{ fontSize: 13, color: V2.navy, fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 13.5, fontWeight: 800, fontFamily: JAI_FONTS.mono, color, flexShrink: 0 }}>
        {deltaPercent > 0 ? '+' : ''}{deltaPercent}%
      </span>
    </div>
  )
}
